import React, { useState, useEffect } from 'react';
import { Form, Button, Breadcrumb, Collapse, InputGroup, Row, Col } from 'react-bootstrap';
import { useHistory, Link } from 'react-router-dom';
import { Formik, FieldArray, Field } from 'formik';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import * as yup from 'yup';
import { FiPlus, FiMinus } from 'react-icons/fi';

import UserService from 'Services/UserService';
import FonctionService from 'Services/FonctionService';
import ServiceService from 'Services/ServiceService';
import PasswordInput from 'Components/UI/PasswordInput';

const schema = (isEditMode) =>
    yup.object().shape({
        userName: yup.string().required('Nom d’utilisateur est requis'),
        email: yup.string().email('Email invalide').required('Email est requis'),
        firstName: yup.string().required('Prénom est requis'),
        lastName: yup.string().required('Nom de famille est requis'),
        password: yup
            .string()
            .min(8, 'Le mot de passe doit contenir au moins 8 caractères')
            .matches(/[A-Z]/, 'Le mot de passe doit contenir au moins une lettre majuscule')
            .matches(/[a-z]/, 'Le mot de passe doit contenir au moins une lettre minuscule')
            .matches(/\d/, 'Le mot de passe doit contenir au moins un chiffre')
            .matches(
                /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;'/~`|]/,
                'Le mot de passe doit contenir au moins un caractère spécial',
            )
            .when([], {
                is: () => !isEditMode,
                then: yup.string().required('Le mot de passe est requis'),
            }),
        fonctionId: yup.string().required('La fonction est requise'),
        admin: yup.boolean(),
        realOrCli: yup.boolean(),
        active: yup.boolean(),
        disabled: yup.boolean(),
        sendMail: yup.boolean(),
        mustChangePassword: yup.boolean(),
        personnelData: yup.object({
            matricule: yup.string(),
            categProf: yup.string(),
            isCLI: yup.boolean(),
            serviceId: yup.number().required(),
            cin: yup.string(),
            cnss: yup.string(),
            diplomes: yup.array().of(yup.string().required()).nullable(),
            niveauEtude: yup.string(),
            anciennete: yup.date(),
            endDate: yup.date(),
            fonction: yup.string(),
        }),
    });

const UserCreateOrEdit = ({ match: { params } }) => {
    const [user, setUser] = useState(null);
    const [fonctions, setFonctions] = useState([]);
    const history = useHistory();

    useEffect(() => {
        FonctionService.getFonctions()
            .then((result) => setFonctions(result.data))
            .catch((error) => console.error(error));

        if (params.userId) {
            UserService.getUserById(params.userId)
                .then((result) => setUser(result.data))
                .catch((error) => console.error(error));
        }
    }, [params.userId]);

    const submitForm = async (data) => {
        try {
            const formValues = {
                userId: params.userId,
                ...data,
                personnelData: {
                    ...data.personnelData,
                    diplomes:
                        data.personnelData && data.personnelData.diplomes.length > 0
                            ? JSON.stringify(data.personnelData.diplomes)
                            : null,
                    anciennete: data.personnelData.anciennete
                        ? dayjs(data.personnelData.anciennete).format('YYYY-MM-DD') + 'T00:00:00'
                        : null,
                    endDate: data.personnelData.endDate
                        ? dayjs(data.personnelData.endDate).format('YYYY-MM-DD') + 'T00:00:00'
                        : null,
                },
            };

            await UserService.saveUser(formValues);
            history.push('/admin/user/index');
            toast.success('Action a été exécutée avec succès.');
        } catch (error) {
            console.error(error);
            toast.error("Une erreur s'est produite !");
        }
    };

    return (
        <>
            <Breadcrumb className="mt-3">
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/' }}>
                    Accueil
                </Breadcrumb.Item>
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/admin' }}>
                    Admin
                </Breadcrumb.Item>
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/admin/user/index' }}>
                    Utilisateurs
                </Breadcrumb.Item>
                <Breadcrumb.Item active>
                    {params.userId ? 'Modifier utilisateur' : 'Créer un nouvel utilisateur'}
                </Breadcrumb.Item>
            </Breadcrumb>
            <div className="row py-3">
                <div className="col text-center">
                    <h2 className="mb-0">{params.userId ? 'Modifier utilisateur' : 'Créer un nouvel utilisateur'}</h2>
                    {params.userId && <i style={{ fontSize: '8pt', fontStyle: 'unset' }}>{params.userId}</i>}
                </div>
            </div>
            <div className="row mb-3">
                <div className="col-md-3"></div>
                <div className="col-md-6">
                    <Formik
                        validationSchema={schema(!!user)}
                        onSubmit={submitForm}
                        enableReinitialize
                        initialValues={{
                            userName: user?.userName || '',
                            firstName: user?.firstName || '',
                            lastName: user?.lastName || '',
                            email: user?.email || '',
                            password: '',
                            fonctionId: user?.fonctionId || '',
                            admin: user?.admin || false,
                            realOrCli: user?.realOrCli || true,
                            active: user?.active || false,
                            disabled: user?.disabled || false,
                            sendMail: false,
                            mustChangePassword: user?.mustChangePassword || false,
                            personnelData: {
                                matricule: user?.personnelData?.matricule || '',
                                categProf: user?.personnelData?.categProf || '',
                                isCLI: user?.personnelData?.isCLI || false,
                                serviceId: user?.personnelData?.serviceId || 1,
                                cin: user?.personnelData?.cin || '',
                                cnss: user?.personnelData?.cnss || '',
                                diplomes:
                                    user &&
                                    user.personnelData &&
                                    user.personnelData.diplomes &&
                                    user.personnelData.diplomes.trim() !== ''
                                        ? JSON.parse(user.personnelData.diplomes)
                                        : [],
                                niveauEtude: user?.personnelData?.niveauEtude || '',
                                anciennete: user?.personnelData?.anciennete
                                    ? new Date(user.personnelData.anciennete).toISOString().split('T')[0]
                                    : '',
                                endDate: user?.personnelData?.endDate
                                    ? new Date(user.personnelData.endDate).toISOString().split('T')[0]
                                    : '',
                                fonction: user?.personnelData?.fonction || '',
                            },
                        }}
                    >
                        {({ handleSubmit, handleChange, values, errors, setFieldValue }) => (
                            <Form noValidate onSubmit={handleSubmit}>
                                <Form.Group controlId="firstName">
                                    <Form.Label>Prénom</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Prénom"
                                        name="firstName"
                                        value={values.firstName}
                                        onChange={handleChange}
                                        isInvalid={!!errors.firstName}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.firstName}</Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group controlId="lastName">
                                    <Form.Label>Nom de famille</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Nom de famille"
                                        name="lastName"
                                        value={values.lastName}
                                        onChange={handleChange}
                                        isInvalid={!!errors.lastName}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.lastName}</Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group controlId="userName">
                                    <Form.Label>Nom d’utilisateur</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Nom d’utilisateur"
                                        name="userName"
                                        value={values.userName}
                                        onChange={handleChange}
                                        isInvalid={!!errors.userName}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.userName}</Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group controlId="email">
                                    <Form.Label>Email</Form.Label>
                                    <Form.Control
                                        type="email"
                                        placeholder="Email"
                                        name="email"
                                        value={values.email}
                                        onChange={handleChange}
                                        isInvalid={!!errors.email}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group controlId="password">
                                    <Form.Label>Mot de passe</Form.Label>
                                    <PasswordInput
                                        name="password"
                                        value={values.password}
                                        onChange={handleChange}
                                        isInvalid={!!errors.password}
                                        autoComplete="new-password"
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.password}</Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group controlId="fonctionId">
                                    <Form.Label>Fonction</Form.Label>
                                    <Form.Control
                                        as="select"
                                        name="fonctionId"
                                        value={values.fonctionId}
                                        onChange={handleChange}
                                        isInvalid={!!errors.fonctionId}
                                    >
                                        <option value="">Sélectionner la fonction</option>
                                        {fonctions.map((fonction) => (
                                            <option key={fonction.fonctionId} value={fonction.fonctionId}>
                                                {fonction.name}
                                            </option>
                                        ))}
                                    </Form.Control>
                                    <Form.Control.Feedback type="invalid">{errors.fonctionId}</Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group controlId="formBasicCheckbox">
                                    <Form.Check
                                        type="checkbox"
                                        label="Admin"
                                        name="admin"
                                        checked={values.admin}
                                        onChange={handleChange}
                                        isInvalid={!!errors.admin}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.admin}</Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group controlId="formBasicCheckboxrealOrCli">
                                    <Form.Check
                                        type="checkbox"
                                        label="RealOrCli"
                                        name="realOrCli"
                                        checked={values.realOrCli}
                                        onChange={handleChange}
                                        isInvalid={!!errors.realOrCli}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.realOrCli}</Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group controlId="formBasicCheckbox2">
                                    <Form.Check
                                        type="checkbox"
                                        label="Actif"
                                        name="active"
                                        checked={values.active}
                                        onChange={(e) => {
                                            handleChange(e);
                                            if (e.target.checked) {
                                                handleChange({
                                                    target: { name: 'sendMail', value: false },
                                                });
                                            }
                                        }}
                                        isInvalid={!!errors.active}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.active}</Form.Control.Feedback>
                                </Form.Group>
                                {user && (
                                    <Form.Group controlId="formBasicCheckbox2ss">
                                        <Form.Check
                                            type="checkbox"
                                            label="Demande de changement du mot de passe"
                                            name="mustChangePassword"
                                            checked={values.mustChangePassword}
                                            onChange={handleChange}
                                            isInvalid={!!errors.mustChangePassword}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.mustChangePassword}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                )}
                                <Form.Group controlId="formBasicCheckbox3">
                                    <Form.Check
                                        type="checkbox"
                                        label="Désactivé"
                                        name="disabled"
                                        checked={values.disabled}
                                        onChange={(e) => {
                                            handleChange(e);
                                            if (e.target.checked) {
                                                handleChange({
                                                    target: { name: 'sendMail', value: false },
                                                });
                                            }
                                        }}
                                        isInvalid={!!errors.disabled}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.disabled}</Form.Control.Feedback>
                                </Form.Group>
                                {!params.userId && (
                                    <Form.Group controlId="formBasicCheckbox4">
                                        <Form.Check
                                            type="checkbox"
                                            label="Envoyer un mail"
                                            name="sendMail"
                                            checked={values.sendMail}
                                            onChange={(e) => {
                                                handleChange(e);
                                                if (e.target.checked) {
                                                    handleChange({
                                                        target: { name: 'active', value: false },
                                                    });
                                                    handleChange({
                                                        target: { name: 'disabled', value: false },
                                                    });
                                                }
                                            }}
                                            isInvalid={!!errors.sendMail}
                                        />
                                        <Form.Control.Feedback type="invalid">{errors.sendMail}</Form.Control.Feedback>
                                    </Form.Group>
                                )}
                                <PersonnelDataForm formik={{ handleChange, values, errors, setFieldValue }} />
                                <Button variant="primary" type="submit" block>
                                    Enregistrer
                                </Button>
                            </Form>
                        )}
                    </Formik>
                </div>
                <div className="col-md-3"></div>
            </div>
        </>
    );
};

export default UserCreateOrEdit;

const PersonnelDataForm = (props) => {
    const [services, setServices] = useState([]);
    const [open, setOpen] = useState(false);
    const { formik } = props;

    useEffect(() => {
        ServiceService.getServices()
            .then((result) => {
                if (result.status === 200) setServices(result.data);
            })
            .catch((e) => console.log(e));
    }, []);

    // Handler to reset the date field to undefined
    const handleResetDate = (field) => {
        formik.setFieldValue(field, undefined);
    };

    return (
        <>
            <div className="d-flex align-items-center justify-content-end">
                <Button
                    onClick={() => setOpen(!open)}
                    aria-controls="personnelDataCollapse"
                    aria-expanded={open}
                    className="mt-3 mb-3"
                    variant="outline-secondary"
                    size="sm"
                >
                    {open ? 'Hide Personnel Data' : 'Show Personnel Data'}
                </Button>
            </div>

            {/* Personnel Data Collapse Section */}
            <Collapse in={open}>
                <div className="personnel-data-section">
                    <Form.Group controlId="formPersonnelDatamatricule">
                        <Form.Label>Matricule</Form.Label>
                        <Form.Control
                            type="text"
                            name="personnelData.matricule"
                            value={formik.values.personnelData.matricule}
                            onChange={formik.handleChange}
                            isInvalid={!!formik.errors.personnelData?.matricule}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formik.errors.personnelData?.matricule}
                        </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group controlId="formPersonnelDatafonction">
                        <Form.Label>Fonction</Form.Label>
                        <Form.Control
                            type="text"
                            name="personnelData.fonction"
                            placeholder="Fonction"
                            value={formik.values.personnelData.fonction}
                            onChange={formik.handleChange}
                            isInvalid={!!formik.errors.personnelData?.fonction}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formik.errors.personnelData?.fonction}
                        </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group controlId="formPersonnelDataserviceId">
                        <Form.Label>Service</Form.Label>

                        <Form.Control
                            as="select"
                            name="personnelData.serviceId"
                            value={formik.values.personnelData.serviceId}
                            onChange={formik.handleChange}
                            isInvalid={!!formik.errors.personnelData?.serviceId}
                        >
                            <option value="">Sélectionner un service</option>
                            {services.map((service) => (
                                <option key={service.serviceId} value={service.serviceId}>
                                    {service.nom}
                                </option>
                            ))}
                        </Form.Control>
                        <Form.Control.Feedback type="invalid">
                            {formik.errors.personnelData?.serviceId}
                        </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group controlId="formPersonnelDatacategProf">
                        <Form.Label>Categorie pro</Form.Label>

                        <Form.Control
                            as="select"
                            name="personnelData.categProf"
                            value={formik.values.personnelData.categProf}
                            onChange={formik.handleChange}
                            isInvalid={!!formik.errors.personnelData?.categProf}
                        >
                            <option value="">Sélectionner une categorie</option>
                            <option value="EMPLOYE">EMPLOYE</option>
                            <option value="CADRE">CADRE</option>
                            <option value="DIRECTEUR">DIRECTEUR</option>
                        </Form.Control>
                        <Form.Control.Feedback type="invalid">
                            {formik.errors.personnelData?.categProf}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group controlId="formPersonnelDataCin">
                        <Form.Label>CIN</Form.Label>
                        <Form.Control
                            type="text"
                            name="personnelData.cin"
                            value={formik.values.personnelData.cin}
                            onChange={formik.handleChange}
                            isInvalid={!!formik.errors.personnelData?.cin}
                        />
                        <Form.Control.Feedback type="invalid">{formik.errors.personnelData?.cin}</Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group controlId="formPersonnelDataCnss">
                        <Form.Label>CNSS</Form.Label>
                        <Form.Control
                            type="text"
                            name="personnelData.cnss"
                            value={formik.values.personnelData.cnss}
                            onChange={formik.handleChange}
                            isInvalid={!!formik.errors.personnelData?.cnss}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formik.errors.personnelData?.cnss}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group controlId="formPersonnelDataDiplomes">
                        <Form.Label>Diplomes</Form.Label>
                        <Button
                            variant="outline-success dynamicJsHoverColorIcon float-right mb-1"
                            onClick={() =>
                                formik.handleChange({
                                    target: {
                                        value: [
                                            ...(formik.values.personnelData.diplomes
                                                ? formik.values.personnelData.diplomes
                                                : []),
                                            '',
                                        ],
                                        name: 'personnelData.diplomes',
                                    },
                                })
                            }
                        >
                            <FiPlus
                                style={{
                                    fontSize: '9pt',
                                    color: 'green',
                                    '--my-hover-icon-color': '#fff',
                                    cursor: 'pointer',
                                }}
                            />
                        </Button>
                        <FieldArray
                            name="personnelData.diplomes"
                            render={() =>
                                formik.values.personnelData.diplomes &&
                                formik.values.personnelData.diplomes.map((rnData, index) => {
                                    const rnErrors =
                                        formik.errors &&
                                        formik.errors.personnelData &&
                                        formik.errors.personnelData.diplomes?.length &&
                                        formik.errors.personnelData.diplomes[index];
                                    return (
                                        <InputGroup key={index} className="mt-1">
                                            <Field
                                                name={`personnelData.diplomes[${index}]`}
                                                type="text"
                                                placeholder="Diplome"
                                                className={`form-control ${!!rnErrors ? ' is-invalid' : ''}`}
                                            />
                                            <Button
                                                variant="outline-warning"
                                                className="inputGrpButton dynamicJsHoverColorIcon"
                                                onClick={() => {
                                                    const updatedArr = [...formik.values.personnelData.diplomes].filter(
                                                        (dvr, i) => i !== index,
                                                    );

                                                    formik.handleChange({
                                                        target: {
                                                            value: updatedArr,
                                                            name: 'personnelData.diplomes',
                                                        },
                                                    });
                                                }}
                                            >
                                                <FiMinus
                                                    style={{
                                                        fontSize: '9pt',
                                                        color: '#ff0000',
                                                        '--my-hover-icon-color': '#fff',
                                                        cursor: 'pointer',
                                                    }}
                                                />
                                            </Button>
                                        </InputGroup>
                                    );
                                })
                            }
                        />
                        <Form.Control.Feedback type="invalid">
                            {formik.errors.personnelData?.diplomes}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group controlId="formPersonnelDataNiveauEtude">
                        <Form.Label>Niveau Etude</Form.Label>
                        <Form.Control
                            type="text"
                            name="personnelData.niveauEtude"
                            value={formik.values.personnelData.niveauEtude}
                            onChange={formik.handleChange}
                            isInvalid={!!formik.errors.personnelData?.niveauEtude}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formik.errors.personnelData?.niveauEtude}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Row>
                        <Form.Group as={Col} md="6" controlId="formPersonnelDataAnciennete">
                            <Form.Label>Anciennete</Form.Label>
                            <InputGroup>
                                <Form.Control
                                    type="date"
                                    name="personnelData.anciennete"
                                    value={formik.values.personnelData.anciennete || ''}
                                    onChange={formik.handleChange}
                                    isInvalid={!!formik.errors.personnelData?.anciennete}
                                />
                                <InputGroup.Append>
                                    <Button
                                        variant="outline-secondary"
                                        onClick={() => handleResetDate('personnelData.anciennete')}
                                    >
                                        Clear
                                    </Button>
                                </InputGroup.Append>
                                <Form.Control.Feedback type="invalid">
                                    {formik.errors.personnelData?.anciennete}
                                </Form.Control.Feedback>
                            </InputGroup>
                        </Form.Group>

                        {/* End Date Field with Reset Button */}
                        <Form.Group as={Col} md="6" controlId="formPersonnelDataEndDate">
                            <Form.Label>End Date</Form.Label>
                            <InputGroup>
                                <Form.Control
                                    type="date"
                                    name="personnelData.endDate"
                                    value={formik.values.personnelData.endDate || ''}
                                    onChange={(e) => {
                                        // console.log(e.target.value);
                                        formik.handleChange(e);
                                    }}
                                    isInvalid={!!formik.errors.personnelData?.endDate}
                                />
                                <InputGroup.Append>
                                    <Button
                                        variant="outline-secondary"
                                        onClick={() => handleResetDate('personnelData.endDate')}
                                    >
                                        Clear
                                    </Button>
                                </InputGroup.Append>
                                <Form.Control.Feedback type="invalid">
                                    {formik.errors.personnelData?.endDate}
                                </Form.Control.Feedback>
                            </InputGroup>
                        </Form.Group>
                    </Row>

                    <Form.Group controlId="formPersonnelDataisCLI">
                        <Form.Check
                            type="checkbox"
                            label="Is CLI"
                            name="personnelData.isCLI"
                            checked={formik.values.personnelData.isCLI}
                            onChange={formik.handleChange}
                            isInvalid={!!formik.errors.personnelData?.isCLI}
                        />
                    </Form.Group>
                </div>
            </Collapse>
        </>
    );
};
